/**
 * AdminReviewsPage ✦ iter228 — /admin/reviews
 *
 * Full CRUD + approval queue + bulk actions + dashboard for reviews.
 * Heritage admin styling (dark, gold accents) — mirrors the other
 * admin surfaces (Heritage, Backdrops, Quotes).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const GOLD = '#D4AF5E';
const GOLD_FAINT = 'rgba(212,170,82,0.22)';
const IVORY = '#FBEED0';
const IVORY_DIM = 'rgba(251,238,208,0.62)';
const IVORY_GHOST = 'rgba(251,238,208,0.42)';

const mediaUrl = (path) =>
  path?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${path}` : path;

const pageStyle = {
  minHeight: '100vh', background: '#0F0B1A', color: IVORY,
  fontFamily: SERIF, padding: 'clamp(40px, 6vw, 70px) clamp(16px, 3vw, 40px) 80px',
};
const inputBase = {
  background: 'rgba(0,0,0,0.34)', border: `1px solid ${GOLD_FAINT}`,
  color: IVORY, fontFamily: SERIF, fontSize: 13.5,
  padding: '8px 10px', borderRadius: 6, width: '100%',
  boxSizing: 'border-box',
};
const ghostPill = {
  background: 'transparent', border: `1px solid ${GOLD_FAINT}`,
  color: IVORY, fontFamily: SERIF, fontSize: 12.5,
  padding: '6px 14px', borderRadius: 999, cursor: 'pointer',
};
const primaryPill = { ...ghostPill, background: 'rgba(212,170,82,0.14)' };
const dangerPill = { ...ghostPill, color: 'rgba(184,111,111,0.92)', border: `1px solid rgba(184,111,111,0.32)` };
const goldPill = { ...ghostPill, background: GOLD, color: '#1A120A', border: `1px solid ${GOLD}` };
const label = {
  display: 'block', fontFamily: SERIF, fontStyle: 'italic',
  color: IVORY_DIM, fontSize: 11.5, letterSpacing: '0.04em', marginBottom: 4,
};

const PRODUCTS = [
  { slug: 'blossom-booth', label: 'Blossom Booth' },
  { slug: 'woodland-mirror-booth', label: 'Mirror Booth' },
  { slug: 'heritage-booth', label: 'The Heritage Booth' },
  { slug: 'woodland-audio-guestbook', label: 'Audio Guestbook' },
  { slug: 'woodland-video-guestbook', label: 'Video Guestbook' },
  { slug: 'woodland-memory-post-box', label: 'Memory Post Box' },
  { slug: 'wedding-styling', label: 'Wedding Styling' },
  { slug: 'other', label: 'Other' },
];
const EVENT_TYPES = ['wedding', 'corporate', 'birthday', 'baby-shower', 'prom',
  'christmas', 'school', 'charity', 'festival', 'other'];
const SOURCES = ['website', 'facebook', 'add_to_event', 'google', 'email', 'other'];

const TABS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'pending', label: 'Pending', filterStatus: 'pending' },
  { key: 'approved', label: 'Approved', filterStatus: 'approved' },
  { key: 'all', label: 'All Reviews' },
  { key: 'add', label: 'Add / Import' },
  { key: 'hero', label: 'Hero Stats' },
];

const AdminReviewsPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [list, setList] = useState([]);
  const [counts, setCounts] = useState({});
  const [dash, setDash] = useState(null);
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterProduct, setFilterProduct] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    document.title = 'Reviews ✦ EventDrop Admin';
    if (!loading && (!user || !user.is_admin)) navigate('/');
  }, [user, loading, navigate]);

  const load = useCallback(async () => {
    const activeTab = TABS.find(t => t.key === tab);
    const status = activeTab?.filterStatus;
    try {
      const params = {};
      if (status) params.status = status;
      if (search) params.search = search;
      if (filterSource) params.source = filterSource;
      if (filterProduct) params.product = filterProduct;
      const [r, t] = await Promise.all([
        axios.get(`${API}/admin/reviews`, { params }),
        axios.get(`${API}/admin/reviews-tags`),
      ]);
      setList(r.data.reviews || []);
      setCounts(r.data.counts || {});
      setAllTags(t.data.tags || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load reviews.');
    }
  }, [tab, search, filterSource, filterProduct]);

  const loadDash = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/reviews-dashboard`);
      setDash(r.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (!user || !user.is_admin) return;
    load();
    if (tab === 'dashboard') loadDash();
  }, [user, tab, load, loadDash]);

  const toggleSel = (id) => {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const bulk = async (action) => {
    if (selected.size === 0) return;
    if (action === 'delete' && !window.confirm(`Delete ${selected.size} reviews?`)) return;
    try {
      await axios.post(`${API}/admin/reviews/bulk`, {
        review_ids: Array.from(selected), action,
      });
      toast.success(`${action} applied to ${selected.size} reviews.`);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk action failed.');
    }
  };

  if (loading) return <div style={pageStyle}>Loading…</div>;
  if (!user || !user.is_admin) return null;

  return (
    <div data-testid="admin-reviews-page" style={pageStyle}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <Header counts={counts} />
        <Tabs tab={tab} setTab={setTab} counts={counts} />
        {tab === 'dashboard' && <DashboardPanel dash={dash} />}
        {tab === 'hero' && <HeroStatsPanel />}
        {tab === 'add' && <AddImportPanel allTags={allTags} onSaved={() => { setTab('all'); load(); }} />}
        {(tab === 'pending' || tab === 'approved' || tab === 'all') && (
          <>
            <FilterBar {...{ search, setSearch, filterSource, setFilterSource,
              filterProduct, setFilterProduct, onApply: load }} />
            {selected.size > 0 && (
              <BulkBar count={selected.size} onAction={bulk} onClear={() => setSelected(new Set())} />
            )}
            <ReviewList list={list} selected={selected} toggleSel={toggleSel}
              allTags={allTags} onChanged={load} />
          </>
        )}
      </div>
    </div>
  );
};

// ─── Header / Tabs ──────────────────────────────────────────────────
const Header = ({ counts }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{
      fontFamily: SERIF, fontStyle: 'italic', fontSize: 11.5,
      letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, marginBottom: 8,
    }}>✦ EventDrop Pro</div>
    <h1 data-testid="admin-reviews-heading" style={{
      fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(28px, 4vw, 38px)',
      color: IVORY, margin: 0, letterSpacing: '-0.015em',
    }}>Reviews</h1>
    <div style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM, marginTop: 6, fontSize: 13.5 }}>
      {counts.pending || 0} pending · {counts.approved || 0} approved · {counts.featured || 0} featured
    </div>
  </div>
);

const Tabs = ({ tab, setTab, counts }) => (
  <div data-testid="admin-reviews-tabs" style={{
    display: 'flex', gap: 6, flexWrap: 'wrap',
    borderBottom: `1px solid ${GOLD_FAINT}`, marginBottom: 22,
  }}>
    {TABS.map(t => {
      const active = tab === t.key;
      const badge = t.key === 'pending' ? counts.pending : null;
      return (
        <button key={t.key} type="button" onClick={() => setTab(t.key)}
          data-testid={`admin-reviews-tab-${t.key}`}
          style={{
            background: active ? 'rgba(212,170,82,0.14)' : 'transparent',
            border: 'none', borderBottom: `2px solid ${active ? GOLD : 'transparent'}`,
            color: active ? IVORY : IVORY_DIM,
            fontFamily: SERIF, fontSize: 13.5, padding: '10px 18px',
            cursor: 'pointer', letterSpacing: '0.04em',
          }}>
          {t.label}{badge ? <span style={{
            marginLeft: 8, background: GOLD, color: '#1A120A',
            padding: '2px 8px', borderRadius: 999, fontSize: 11,
          }}>{badge}</span> : null}
        </button>
      );
    })}
  </div>
);

// ─── Dashboard ──────────────────────────────────────────────────────
const DashboardPanel = ({ dash }) => {
  if (!dash) return <div style={{ color: IVORY_DIM, fontStyle: 'italic' }}>Loading…</div>;
  const t = dash.totals || {};
  return (
    <div data-testid="admin-reviews-dashboard" style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div style={{
        display: 'grid', gap: 16,
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      }}>
        <Tile label="Total reviews" value={t.total || 0} testid="dash-total" />
        <Tile label="Approved" value={t.approved || 0} testid="dash-approved" />
        <Tile label="Pending" value={t.pending || 0} testid="dash-pending" highlight={t.pending > 0} />
        <Tile label="Featured" value={t.featured || 0} testid="dash-featured" />
        <Tile label="On homepage" value={t.homepage || 0} testid="dash-homepage" />
        <Tile label="This month" value={t.this_month || 0} testid="dash-month" />
        <Tile label="Average rating" value={t.average_rating ? `${t.average_rating} ★` : '—'} testid="dash-avg-rating" />
        <Tile label="Most reviewed" value={dash.most_reviewed_product || '—'} testid="dash-most-product" small />
        <Tile label="Highest rated" value={dash.highest_rated_product || '—'} testid="dash-highest-product" small />
      </div>
      <Card title="Reviews by product"><DistList map={dash.by_product} /></Card>
      <Card title="Reviews by source"><DistList map={dash.by_source} /></Card>
      <Card title="Reviews by event type"><DistList map={dash.by_event_type} /></Card>
      <Card title="Reviews by year"><DistList map={dash.by_year} /></Card>
      <MaintenancePanel />
      <Card title="Recent reviews">
        {(dash.recent_reviews || []).map(r => (
          <div key={r.review_id} style={{
            padding: '10px 0', borderBottom: `1px solid ${GOLD_FAINT}`,
            display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'baseline',
          }}>
            <div>
              <span style={{ color: GOLD, marginRight: 8 }}>{'★'.repeat(r.rating)}</span>
              <strong style={{ color: IVORY }}>{r.customer_name}</strong>
              <span style={{ color: IVORY_GHOST, fontStyle: 'italic', marginLeft: 8, fontSize: 12 }}>
                {r.product || r.event_type || ''}
              </span>
            </div>
            <span style={{ color: IVORY_GHOST, fontSize: 11.5, fontStyle: 'italic' }}>{r.status}</span>
          </div>
        ))}
      </Card>
    </div>
  );
};

const Tile = ({ label, value, testid, highlight = false, small = false }) => (
  <div data-testid={testid} style={{
    background: 'rgba(0,0,0,0.32)', border: `1px solid ${highlight ? GOLD : GOLD_FAINT}`,
    borderRadius: 12, padding: '18px 20px',
  }}>
    <div style={{
      fontFamily: SERIF, fontStyle: 'italic', fontSize: 11.5,
      letterSpacing: '0.04em', color: IVORY_DIM, marginBottom: 6,
    }}>{label}</div>
    <div style={{
      fontFamily: SERIF, fontWeight: 500,
      fontSize: small ? 17 : 26, color: highlight ? GOLD : IVORY,
      letterSpacing: '-0.01em',
    }}>{value}</div>
  </div>
);

const Card = ({ title, children }) => (
  <div style={{
    background: 'rgba(0,0,0,0.28)', border: `1px solid ${GOLD_FAINT}`,
    borderRadius: 12, padding: '18px 20px',
  }}>
    <div style={{
      fontFamily: SERIF, fontStyle: 'italic', fontSize: 12,
      letterSpacing: '0.2em', textTransform: 'uppercase',
      color: GOLD, marginBottom: 12,
    }}>{title}</div>
    {children}
  </div>
);

// iter236 · Backfill Map Pins — one-shot maintenance action.
// Idempotent so the button is safe to press any number of times.
const MaintenancePanel = () => {
  const [running, setRunning] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const run = async () => {
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/reviews/backfill-map-pins`,
        {},
        { withCredentials: true },
      );
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Backfill failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card title="Map pin maintenance">
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ fontSize: 13, color: IVORY_DIM, lineHeight: 1.55 }}>
          Scans every approved review, geocodes any that have a postcode but no
          coords, and quietly pins their venue on the coverage map. Safe to run
          repeatedly — venues that already carry coordinates are skipped.
        </div>
        <div>
          <button
            type="button"
            onClick={run}
            disabled={running}
            data-testid="backfill-map-pins-btn"
            style={{ ...primaryPill, opacity: running ? 0.55 : 1 }}
          >
            {running ? 'Backfilling…' : '✦ Backfill map pins'}
          </button>
        </div>
        {error && (
          <div
            data-testid="backfill-map-pins-error"
            style={{ color: '#F08D8D', fontSize: 13 }}
          >
            {error}
          </div>
        )}
        {result && (
          <div
            data-testid="backfill-map-pins-result"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 10, marginTop: 4,
            }}
          >
            {[
              ['Scanned', result.reviews_scanned],
              ['Pins created', result.pins_created],
              ['Pins updated', result.pins_updated],
              ['Newly geocoded', result.geocoded],
              ['Skipped', result.skipped],
            ].map(([label, value]) => (
              <div
                key={label}
                style={{
                  background: 'rgba(0,0,0,0.32)',
                  border: `1px solid ${GOLD_FAINT}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                }}
              >
                <div style={{
                  fontFamily: SERIF, fontStyle: 'italic', fontSize: 11,
                  letterSpacing: '0.05em', color: IVORY_DIM, marginBottom: 4,
                }}>{label}</div>
                <div style={{
                  fontFamily: SERIF, fontSize: 20,
                  color: value > 0 ? GOLD : IVORY, fontWeight: 500,
                }}>{value ?? 0}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

const DistList = ({ map }) => {
  const items = Object.entries(map || {}).sort((a, b) => b[1] - a[1]);
  if (items.length === 0) return <em style={{ color: IVORY_GHOST }}>No data yet.</em>;
  const max = items[0][1];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 140, fontSize: 12.5, color: IVORY }}>{k}</span>
          <div style={{
            flex: 1, height: 6, background: 'rgba(212,170,82,0.10)', borderRadius: 999,
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${(v / max) * 100}%`, height: '100%', background: GOLD,
            }} />
          </div>
          <span style={{ width: 36, textAlign: 'right', fontSize: 12, color: IVORY_DIM }}>{v}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Hero stats overrides ───────────────────────────────────────────
const HeroStatsPanel = () => {
  const [form, setForm] = useState({
    happy_clients: '', photos_captured: '', events_covered: '',
    years_trading: '', custom_line_4: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/reviews-hero-stats`);
        setForm((f) => ({ ...f, ...r.data }));
      } catch (e) { /* silent */ }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/reviews-hero-stats`, form);
      toast.success('Hero stats saved ✦');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="hero-stats-panel" style={{
      background: 'rgba(0,0,0,0.28)', border: `1px solid ${GOLD_FAINT}`,
      borderRadius: 12, padding: '22px 24px', maxWidth: 720,
    }}>
      <p style={{
        fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM, marginTop: 0,
      }}>
        Average rating &amp; total reviews are auto-calculated from real data.
        These fields let you craft the rest of the hero banner.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {[
          ['happy_clients', 'Happy Clients (e.g. 50+)'],
          ['photos_captured', 'Photos Captured (e.g. 10,000+)'],
          ['events_covered', 'Events Line (e.g. Weddings · Parties · Corporate)'],
          ['years_trading', 'Years Trading (e.g. 6)'],
        ].map(([k, lbl]) => (
          <div key={k}>
            <label style={label}>{lbl}</label>
            <input value={form[k] || ''} onChange={upd(k)} style={inputBase}
              data-testid={`hero-stat-input-${k}`} />
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14 }}>
        <label style={label}>Optional extra line</label>
        <input value={form.custom_line_4 || ''} onChange={upd('custom_line_4')} style={inputBase}
          data-testid="hero-stat-input-custom_line_4" />
      </div>
      <div style={{ marginTop: 18, textAlign: 'right' }}>
        <button type="button" onClick={save} disabled={saving}
          data-testid="hero-stats-save"
          style={{ ...goldPill, opacity: saving ? 0.5 : 1 }}>
          {saving ? 'Saving…' : 'Save hero stats ✦'}
        </button>
      </div>
    </div>
  );
};

// ─── Filter bar / Bulk bar ──────────────────────────────────────────
const FilterBar = ({ search, setSearch, filterSource, setFilterSource,
                    filterProduct, setFilterProduct, onApply }) => (
  <div data-testid="admin-reviews-filter-bar" style={{
    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 10, marginBottom: 16,
  }}>
    <input placeholder="Search name, venue, text…" value={search}
      onChange={(e) => setSearch(e.target.value)}
      onKeyDown={(e) => e.key === 'Enter' && onApply()}
      data-testid="admin-reviews-search" style={inputBase} />
    <select value={filterProduct} onChange={(e) => { setFilterProduct(e.target.value); }}
      data-testid="admin-reviews-filter-product" style={inputBase}>
      <option value="">All products</option>
      {PRODUCTS.map(p => <option key={p.slug} value={p.slug}>{p.label}</option>)}
    </select>
    <select value={filterSource} onChange={(e) => { setFilterSource(e.target.value); }}
      data-testid="admin-reviews-filter-source" style={inputBase}>
      <option value="">All sources</option>
      {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
    </select>
    <button type="button" onClick={onApply} data-testid="admin-reviews-apply-filter" style={primaryPill}>
      Apply
    </button>
  </div>
);

const BulkBar = ({ count, onAction, onClear }) => (
  <div data-testid="admin-reviews-bulk-bar" style={{
    display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: 14,
    background: 'rgba(212,170,82,0.10)', border: `1px solid ${GOLD_FAINT}`,
    borderRadius: 10, marginBottom: 14,
  }}>
    <span style={{ color: IVORY, fontSize: 13 }}>✦ {count} selected</span>
    <button type="button" onClick={() => onAction('approve')} style={primaryPill}>Approve</button>
    <button type="button" onClick={() => onAction('reject')} style={ghostPill}>Reject</button>
    <button type="button" onClick={() => onAction('feature')} style={primaryPill}>★ Feature</button>
    <button type="button" onClick={() => onAction('unfeature')} style={ghostPill}>Unfeature</button>
    <button type="button" onClick={() => onAction('homepage_on')} style={primaryPill}>Show on homepage</button>
    <button type="button" onClick={() => onAction('homepage_off')} style={ghostPill}>Remove from homepage</button>
    <button type="button" onClick={() => onAction('hide')} style={ghostPill}>Hide from /reviews</button>
    <button type="button" onClick={() => onAction('show')} style={ghostPill}>Show on /reviews</button>
    <button type="button" onClick={() => onAction('delete')} style={dangerPill}>Delete</button>
    <button type="button" onClick={onClear} style={{ ...ghostPill, marginLeft: 'auto' }}>Clear</button>
  </div>
);

// ─── Review list / row ──────────────────────────────────────────────
const ReviewList = ({ list, selected, toggleSel, allTags, onChanged }) => {
  if (list.length === 0) {
    return <em style={{ color: IVORY_GHOST, fontStyle: 'italic' }}>No reviews match.</em>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {list.map(r => (
        <ReviewRow key={r.review_id} review={r}
          selected={selected.has(r.review_id)}
          toggleSel={() => toggleSel(r.review_id)}
          allTags={allTags} onChanged={onChanged} />
      ))}
    </div>
  );
};

const ReviewRow = ({ review, selected, toggleSel, allTags, onChanged }) => {
  const [open, setOpen] = useState(false);
  const quickAction = async (patch, msg) => {
    try {
      await axios.patch(`${API}/admin/reviews/${review.review_id}`, patch);
      toast.success(msg);
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed.');
    }
  };
  return (
    <div data-testid={`review-row-${review.review_id}`} style={{
      background: 'rgba(0,0,0,0.32)', border: `1px solid ${review.is_featured ? GOLD : GOLD_FAINT}`,
      borderRadius: 12, padding: '16px 18px',
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 14, alignItems: 'start' }}>
        <input type="checkbox" checked={selected} onChange={toggleSel}
          data-testid={`review-row-select-${review.review_id}`}
          style={{ marginTop: 4, accentColor: GOLD, width: 16, height: 16 }} />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ color: GOLD, fontSize: 14 }}>{'★'.repeat(review.rating)}</span>
            <strong style={{ color: IVORY, fontSize: 15 }}>{review.customer_name}</strong>
            <StatusPill status={review.status} />
            {review.booking_verified && <Badge>✓ Verified booking</Badge>}
            {review.is_featured && <Badge>★ Featured</Badge>}
            {review.on_homepage && <Badge>Homepage</Badge>}
            <span style={{ color: IVORY_GHOST, fontSize: 12, fontStyle: 'italic' }}>
              {[review.product, review.event_type, review.venue].filter(Boolean).join(' · ')}
            </span>
          </div>
          <p style={{
            color: IVORY, fontFamily: SERIF, fontStyle: 'italic',
            margin: '8px 0 0', fontSize: 13.5, lineHeight: 1.5,
          }}>
            "{review.review_text.length > 220 && !open
                ? `${review.review_text.slice(0, 220)}…` : review.review_text}"
          </p>
          {(review.image_urls || []).length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              {review.image_urls.map((u, i) => (
                <div key={i} style={{
                  width: 56, height: 56, borderRadius: 6,
                  backgroundImage: `url(${mediaUrl(u)})`,
                  backgroundSize: 'cover', backgroundPosition: 'center',
                }} />
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
          {review.status !== 'approved' && (
            <button type="button" onClick={() => quickAction({ status: 'approved' }, 'Approved ✦')}
              data-testid={`review-approve-${review.review_id}`} style={goldPill}>
              ✓ Approve
            </button>
          )}
          {review.status !== 'rejected' && (
            <button type="button" onClick={() => quickAction({ status: 'rejected' }, 'Rejected')}
              data-testid={`review-reject-${review.review_id}`} style={ghostPill}>
              ✗ Reject
            </button>
          )}
          <button type="button" onClick={() => quickAction(
            { is_featured: !review.is_featured }, review.is_featured ? 'Unfeatured' : '★ Featured')}
            data-testid={`review-toggle-featured-${review.review_id}`} style={primaryPill}>
            {review.is_featured ? 'Unfeature' : '★ Feature'}
          </button>
          <button type="button" onClick={() => quickAction(
            { on_homepage: !review.on_homepage }, review.on_homepage ? 'Removed from homepage' : 'Added to homepage')}
            data-testid={`review-toggle-homepage-${review.review_id}`} style={primaryPill}>
            {review.on_homepage ? 'Remove from homepage' : 'Add to homepage'}
          </button>
          <button type="button" onClick={() => setOpen(o => !o)}
            data-testid={`review-edit-${review.review_id}`} style={ghostPill}>
            {open ? 'Close ✦' : 'Edit ✦'}
          </button>
        </div>
      </div>
      {open && <ReviewEditor review={review} allTags={allTags} onSaved={async () => { setOpen(false); await onChanged(); }} />}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    pending: { bg: 'rgba(212,170,82,0.18)', col: GOLD },
    approved: { bg: 'rgba(125,181,140,0.18)', col: '#CFEFC6' },
    rejected: { bg: 'rgba(184,111,111,0.18)', col: 'rgba(228,170,170,0.95)' },
  }[status] || { bg: 'rgba(255,255,255,0.10)', col: IVORY };
  return (
    <span data-testid={`status-pill-${status}`} style={{
      background: map.bg, color: map.col, padding: '2px 9px', borderRadius: 999,
      fontFamily: SERIF, fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
    }}>{status}</span>
  );
};

const Badge = ({ children }) => (
  <span style={{
    background: 'rgba(212,170,82,0.18)', color: GOLD,
    padding: '2px 9px', borderRadius: 999,
    fontFamily: SERIF, fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
  }}>{children}</span>
);

// ─── Inline editor (full Edit) ──────────────────────────────────────
const ReviewEditor = ({ review, allTags, onSaved }) => {
  const [form, setForm] = useState({
    customer_name: review.customer_name || '',
    rating: review.rating || 5,
    review_text: review.review_text || '',
    product: review.product || '',
    event_type: review.event_type || '',
    venue: review.venue || '',
    event_date: review.event_date || '',
    review_source: review.review_source || 'website',
    tags: review.tags || [],
    display_order: review.display_order || 0,
    on_product_page: !!review.on_product_page,
    on_reviews_page: review.on_reviews_page !== false,
  });
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/reviews/${review.review_id}`, form);
      toast.success('Saved ✦');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${GOLD_FAINT}` }}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
        <div><label style={label}>Name</label>
          <input value={form.customer_name} onChange={upd('customer_name')} style={inputBase} /></div>
        <div><label style={label}>Rating</label>
          <select value={form.rating} onChange={(e) => setForm(f => ({ ...f, rating: Number(e.target.value) }))} style={inputBase}>
            {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} ★</option>)}
          </select></div>
        <div><label style={label}>Source</label>
          <select value={form.review_source} onChange={upd('review_source')} style={inputBase}>
            {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select></div>
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={label}>Review</label>
        <textarea value={form.review_text} onChange={upd('review_text')} rows={4}
          style={{ ...inputBase, resize: 'vertical', minHeight: 80 }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
        <div><label style={label}>Product</label>
          <select value={form.product} onChange={upd('product')} style={inputBase}>
            <option value="">— None —</option>
            {PRODUCTS.map(p => <option key={p.slug} value={p.slug}>{p.label}</option>)}
          </select></div>
        <div><label style={label}>Event type</label>
          <select value={form.event_type} onChange={upd('event_type')} style={inputBase}>
            <option value="">— None —</option>
            {EVENT_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
          </select></div>
        <div><label style={label}>Venue</label>
          <input value={form.venue} onChange={upd('venue')} style={inputBase} /></div>
        <div><label style={label}>Event date</label>
          <input type="date" value={form.event_date} onChange={upd('event_date')} style={{ ...inputBase, colorScheme: 'dark' }} /></div>
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={label}>Tags (type to add, click to remove · autocomplete shown)</label>
        <TagEditor tags={form.tags} setTags={(t) => setForm(f => ({ ...f, tags: t }))} allTags={allTags} />
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: IVORY, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.on_product_page} onChange={upd('on_product_page')} style={{ accentColor: GOLD }} />
          Show on Product page
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: IVORY, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.on_reviews_page} onChange={upd('on_reviews_page')} style={{ accentColor: GOLD }} />
          Show on /reviews
        </label>
        <span style={{ marginLeft: 'auto' }}>
          <button type="button" onClick={save} disabled={saving} style={{ ...goldPill, opacity: saving ? 0.5 : 1 }}>
            {saving ? 'Saving…' : 'Save changes ✦'}
          </button>
        </span>
      </div>
    </div>
  );
};

// ─── Tag editor with free-form + autocomplete ───────────────────────
const TagEditor = ({ tags, setTags, allTags }) => {
  const [input, setInput] = useState('');
  const suggestions = useMemo(() => {
    if (!input.trim()) return [];
    const q = input.trim().toLowerCase();
    return (allTags || []).filter(t =>
      t.toLowerCase().includes(q) && !tags.includes(t)
    ).slice(0, 8);
  }, [input, allTags, tags]);
  const add = (t) => {
    const v = (t || input).trim();
    if (!v || tags.includes(v)) return;
    setTags([...tags, v]);
    setInput('');
  };
  return (
    <div data-testid="tag-editor">
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
        {tags.map(t => (
          <span key={t} onClick={() => setTags(tags.filter(x => x !== t))}
            data-testid={`tag-chip-${t}`}
            style={{
              background: 'rgba(212,170,82,0.16)', color: IVORY,
              padding: '4px 10px', borderRadius: 999, fontSize: 12,
              cursor: 'pointer', border: `1px solid ${GOLD_FAINT}`,
            }}>{t} ×</span>
        ))}
      </div>
      <input value={input} onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        placeholder="Type a tag and press Enter"
        data-testid="tag-input" style={inputBase} />
      {suggestions.length > 0 && (
        <div style={{
          marginTop: 4, background: 'rgba(0,0,0,0.5)',
          border: `1px solid ${GOLD_FAINT}`, borderRadius: 6,
          padding: 6, display: 'flex', flexWrap: 'wrap', gap: 6,
        }}>
          {suggestions.map(s => (
            <button key={s} type="button" onClick={() => add(s)}
              style={{ ...ghostPill, fontSize: 11.5, padding: '4px 10px' }}>{s}</button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Add / Import panel ─────────────────────────────────────────────
const AddImportPanel = ({ allTags, onSaved }) => {
  const [form, setForm] = useState({
    customer_name: '', rating: 5, review_text: '',
    product: '', event_type: '', venue: '', event_date: '',
    review_source: 'facebook', tags: [], display_order: 0,
    is_featured: false, on_homepage: false, on_product_page: false,
    status: 'approved',
  });
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const upd = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/reviews`, {
        ...form,
        rating: Number(form.rating),
        image_urls: photos,
      });
      toast.success(`Review saved — ${r.data.review.status} ✦`);
      setForm({
        customer_name: '', rating: 5, review_text: '',
        product: '', event_type: '', venue: '', event_date: '',
        review_source: form.review_source, tags: [], display_order: 0,
        is_featured: false, on_homepage: false, on_product_page: false,
        status: 'approved',
      });
      setPhotos([]);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const uploadOne = async (file) => {
    if (!file) return;
    if (photos.length >= 5) { toast.error('Max 5 photos.'); return; }
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/reviews/photo-upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotos((p) => [...p, r.data.image_url]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div data-testid="admin-reviews-add-panel" style={{
      background: 'rgba(0,0,0,0.28)', border: `1px solid ${GOLD_FAINT}`,
      borderRadius: 12, padding: '22px 24px',
    }}>
      <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM, marginTop: 0 }}>
        Use this form to <strong>import existing reviews</strong> from Add To Event, Facebook,
        Google, or to manually add a new review. Imported reviews default to <em>approved</em>
        so they appear immediately.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 10 }}>
        <div><label style={label}>Customer name</label>
          <input value={form.customer_name} onChange={upd('customer_name')} style={inputBase}
            data-testid="add-review-name" /></div>
        <div><label style={label}>Rating</label>
          <select value={form.rating} onChange={upd('rating')} style={inputBase}
            data-testid="add-review-rating">
            {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} ★</option>)}
          </select></div>
        <div><label style={label}>Source</label>
          <select value={form.review_source} onChange={upd('review_source')} style={inputBase}
            data-testid="add-review-source">
            {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select></div>
        <div><label style={label}>Status</label>
          <select value={form.status} onChange={upd('status')} style={inputBase}>
            <option value="approved">Approved (live)</option>
            <option value="pending">Pending</option>
          </select></div>
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={label}>Review</label>
        <textarea value={form.review_text} onChange={upd('review_text')} rows={5}
          data-testid="add-review-text"
          style={{ ...inputBase, resize: 'vertical', minHeight: 100 }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
        <div><label style={label}>Product</label>
          <select value={form.product} onChange={upd('product')} style={inputBase}>
            <option value="">— None —</option>
            {PRODUCTS.map(p => <option key={p.slug} value={p.slug}>{p.label}</option>)}
          </select></div>
        <div><label style={label}>Event type</label>
          <select value={form.event_type} onChange={upd('event_type')} style={inputBase}>
            <option value="">— None —</option>
            {EVENT_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
          </select></div>
        <div><label style={label}>Venue</label>
          <input value={form.venue} onChange={upd('venue')} style={inputBase} /></div>
        <div><label style={label}>Event date</label>
          <input type="date" value={form.event_date} onChange={upd('event_date')}
            style={{ ...inputBase, colorScheme: 'dark' }} /></div>
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={label}>Tags</label>
        <TagEditor tags={form.tags} setTags={(t) => setForm(f => ({ ...f, tags: t }))} allTags={allTags} />
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={label}>Photos (max 5)</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {photos.map((u, i) => (
            <div key={i} style={{
              position: 'relative', width: 60, height: 60, borderRadius: 6,
              backgroundImage: `url(${mediaUrl(u)})`, backgroundSize: 'cover', backgroundPosition: 'center',
              border: `1px solid ${GOLD_FAINT}`,
            }}>
              <button type="button" onClick={() => setPhotos(p => p.filter((_, j) => j !== i))}
                style={{
                  position: 'absolute', top: -7, right: -7, width: 20, height: 20,
                  borderRadius: '50%', border: 'none', background: '#1A120A',
                  color: GOLD, cursor: 'pointer', fontSize: 13, lineHeight: '18px',
                }}>×</button>
            </div>
          ))}
          {photos.length < 5 && (
            <label style={{
              cursor: 'pointer', width: 60, height: 60, borderRadius: 6,
              border: `1px dashed ${GOLD_FAINT}`, color: GOLD, fontSize: 22,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              +
              <input ref={fileRef} type="file" accept="image/*" hidden
                onChange={(e) => uploadOne(e.target.files?.[0])} />
            </label>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 16, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: IVORY }}>
          <input type="checkbox" checked={form.is_featured} onChange={upd('is_featured')} style={{ accentColor: GOLD }} /> ★ Featured
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: IVORY }}>
          <input type="checkbox" checked={form.on_homepage} onChange={upd('on_homepage')} style={{ accentColor: GOLD }} /> Homepage
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: IVORY }}>
          <input type="checkbox" checked={form.on_product_page} onChange={upd('on_product_page')} style={{ accentColor: GOLD }} /> Product page
        </label>
        <span style={{ marginLeft: 'auto' }}>
          <button type="button" onClick={save} disabled={saving || !form.customer_name || !form.review_text}
            data-testid="add-review-save"
            style={{ ...goldPill, opacity: (saving || !form.customer_name || !form.review_text) ? 0.5 : 1 }}>
            {saving ? 'Saving…' : 'Save review ✦'}
          </button>
        </span>
      </div>
    </div>
  );
};

export default AdminReviewsPage;
